import { Decimal as DecimalJS } from 'decimal.js';
import { ulid } from 'ulid';

DecimalJS.set({ precision: 38, rounding: DecimalJS.ROUND_HALF_EVEN });

type DecimalValue = string | number | DecimalJS | Decimal;

export class Decimal {
  private readonly _value: DecimalJS;

  constructor(value: DecimalValue) {
    if (value instanceof Decimal) {
      this._value = value._value;
    } else {
      this._value = new DecimalJS(value);
    }
  }

  static zero(): Decimal {
    return new Decimal(0);
  }

  static fromString(s: string): Decimal {
    return new Decimal(s);
  }

  static max(a: Decimal, b: Decimal): Decimal {
    return a.gt(b) ? a : b;
  }

  static min(a: Decimal, b: Decimal): Decimal {
    return a.lt(b) ? a : b;
  }

  private static fromDecimalJS(d: DecimalJS): Decimal {
    const result = Object.create(Decimal.prototype);
    result._value = d;
    return result;
  }

  plus(other: Decimal): Decimal {
    return Decimal.fromDecimalJS(this._value.plus(other._value));
  }

  minus(other: Decimal): Decimal {
    return Decimal.fromDecimalJS(this._value.minus(other._value));
  }

  times(other: Decimal): Decimal {
    return Decimal.fromDecimalJS(this._value.times(other._value));
  }

  div(other: Decimal): Decimal {
    return Decimal.fromDecimalJS(this._value.div(other._value));
  }

  /** Deterministic decimal exponentiation for financial discount factors. */
  pow(exponent: Decimal | number): Decimal {
    return Decimal.fromDecimalJS(this._value.pow(exponent instanceof Decimal ? exponent._value : exponent));
  }

  abs(): Decimal {
    return Decimal.fromDecimalJS(this._value.abs());
  }

  neg(): Decimal {
    return Decimal.fromDecimalJS(this._value.neg());
  }

  cmp(other: Decimal): -1 | 0 | 1 {
    const result = this._value.cmp(other._value);
    return result as -1 | 0 | 1;
  }

  eq(other: Decimal): boolean {
    return this._value.eq(other._value);
  }

  gt(other: Decimal): boolean {
    return this._value.gt(other._value);
  }

  gte(other: Decimal): boolean {
    return this._value.gte(other._value);
  }

  lt(other: Decimal): boolean {
    return this._value.lt(other._value);
  }

  lte(other: Decimal): boolean {
    return this._value.lte(other._value);
  }

  isZero(): boolean {
    return this._value.isZero();
  }

  isPositive(): boolean {
    return this._value.isPositive();
  }

  isNegative(): boolean {
    return this._value.isNegative();
  }

  toFixed(dp: number): string {
    return this._value.toFixed(dp);
  }

  toNumber(): number {
    return this._value.toNumber();
  }

  toString(): string {
    return this._value.toString();
  }

  toDP(dp: number, roundingMode?: DecimalJS.Rounding): Decimal {
    return Decimal.fromDecimalJS(this._value.toDP(dp, roundingMode ?? DecimalJS.ROUND_HALF_EVEN));
  }

  /**
   * Explicit alias matching decimal.js terminology. This keeps financial-domain
   * code readable while preserving the same global half-even rounding policy.
   */
  toDecimalPlaces(dp: number, roundingMode?: DecimalJS.Rounding): Decimal {
    return this.toDP(dp, roundingMode);
  }
}

export const ROUNDING = {
  AMOUNT: 4,
  QUANTITY: 4,
  PRICE: 4,
  RATE: 8,
  FX: 6,
  WACC: 8,
} as const;

export function roundAmount(value: Decimal): Decimal {
  return value.toDP(ROUNDING.AMOUNT);
}

export function roundQuantity(value: Decimal): Decimal {
  return value.toDP(ROUNDING.QUANTITY);
}

export function roundPrice(value: Decimal): Decimal {
  return value.toDP(ROUNDING.PRICE);
}

export function roundRate(value: Decimal): Decimal {
  return value.toDP(ROUNDING.RATE);
}

export function roundFx(value: Decimal): Decimal {
  return value.toDP(ROUNDING.FX);
}

export function generateUlid(): string {
  return ulid();
}
